import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { HidropowerProjectTechnology } from '@/features/projects/types';
import { getDateFromQuarterAndYear, getQuarterFromDate, getYearFromDate, nullableStringSetter } from '@/helpers';
import { useDevelopmentStatus } from '@/hooks';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type HidropowerFormFields = {
  installed_capacity: number | null;
  type_project: string | null;
  storage_hours: number | null;
  specific_production: number | null;
  development_status: string | null;
  ready_to_build: any;
  ready_to_build_quarter: string | null;
  ready_to_build_year: number | null;
  commercial_operation_date: any;
  commercial_operation_date_quarter: string | null;
  commercial_operation_date_year: number | null;
  turbine_capacity: number | null;
  pump_capacity: number | null;
  generation_storage_capacity: number | null;
  comments: string | null;
};

export type HidropowerSubmitFields = {
  installed_capacity: number | null;
  type_project: string | null;
  storage_hours: number | null;
  specific_production: number | null;
  development_status: string | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  turbine_capacity: number | null;
  pump_capacity: number | null;
  generation_storage_capacity: number | null;
  comments: string | null;
};

type HidropowerFormProps = {
  readonly id: string;
  readonly item: HidropowerProjectTechnology;
  readonly onSubmit: (data: HidropowerSubmitFields) => void;
};

export function HidropowerForm({ id, item, onSubmit }: HidropowerFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const quarterOptions = [
    { label: 'Q1', value: '1' },
    { label: 'Q2', value: '2' },
    { label: 'Q3', value: '3' },
    { label: 'Q4', value: '4' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        installed_capacity: item.installed_capacity ?? null,
        type_project: item?.type_project ?? null,
        development_status: item.development_status ?? null,
        specific_production: item.specific_production ?? null,
        ready_to_build: item.ready_to_build ?? null,
        ready_to_build_quarter: getQuarterFromDate(item.ready_to_build),
        ready_to_build_year: getYearFromDate(item.ready_to_build),
        commercial_operation_date_quarter: getQuarterFromDate(item.commercial_operation_date),
        commercial_operation_date_year: getYearFromDate(item.commercial_operation_date),
        commercial_operation_date: item.commercial_operation_date ?? null,
        storage_hours: item?.storage_hours ?? null,
        turbine_capacity: item?.turbine_capacity ?? null,
        pump_capacity: item?.pump_capacity ?? null,
        generation_storage_capacity: item?.generation_storage_capacity ?? null,
        comments: item.comments ?? null,
      },
    }),
    [item]
  );

  const onSubmitHidropowerForm = useCallback(
    (data: HidropowerFormFields) => {
      data.ready_to_build = getDateFromQuarterAndYear(data.ready_to_build_quarter, data.ready_to_build_year);
      data.commercial_operation_date = getDateFromQuarterAndYear(
        data.commercial_operation_date_quarter,
        data.commercial_operation_date_year
      );

      const payload = {
        installed_capacity: data.installed_capacity,
        type_project: data.type_project,
        storage_hours: data.storage_hours,
        specific_production: data.specific_production,
        development_status: data.development_status,
        ready_to_build: data.ready_to_build,
        commercial_operation_date: data.commercial_operation_date,
        turbine_capacity: data.turbine_capacity,
        pump_capacity: data.pump_capacity,
        generation_storage_capacity: data.generation_storage_capacity,
        comments: data.comments,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<HidropowerFormFields> id={id} onSubmit={(data) => onSubmitHidropowerForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 mb-10 italic">
            {t(
              'Please fill in this section with the main details about the project, including technology, development status, installed capacity, ready-to-build (RtB) and COD dates, etc.'
            )}
          </Typography>

          <Field
            id="installed_capacity"
            label={t('Installed Capacity')}
            error={formState.errors['installed_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="installed_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 379')}
              color={formState.errors['installed_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('installed_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="type_project"
            label={t('Type of Project')}
            error={formState.errors['type_project']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="type_project"
              type="text"
              color={formState.errors['type_project'] ? 'error' : 'primary'}
              registration={register('type_project', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="storage_hours"
              label={t('Storage Hours')}
              error={formState.errors['storage_hours']}
              delay={0.15}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                id="storage_hours"
                type="number"
                suffix="h"
                placeholder={t('Example: 8')}
                color={formState.errors['storage_hours'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('storage_hours', { ...NUMERIC })}
              />
            </Field>

            <Field
              id="specific_production"
              label={t('Specific Production')}
              error={formState.errors['specific_production']}
              delay={0.15}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                id="specific_production"
                type="number"
                suffix="h"
                placeholder={t('Example: 45')}
                color={formState.errors['specific_production'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('specific_production', { ...NUMERIC })}
              />
            </Field>
          </div>

          <Field
            id="development_status"
            label={t('Development status')}
            error={formState.errors['development_status']}
            delay={0}
          >
            <Select
              id="development_status"
              registration={register('development_status', { setValueAs: nullableStringSetter })}
              options={developmentStatusOptions}
              placeholder={t('Select a development status')}
              color={formState.errors['development_status'] ? 'error' : 'primary'}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="ready_to_build"
              label={t('Ready to Build Date')}
              error={formState.errors['ready_to_build_quarter'] || formState.errors['ready_to_build_year']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="ready_to_build_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['ready_to_build_quarter'] ? 'error' : 'primary'}
                  registration={register('ready_to_build_quarter')}
                />
                <Input
                  id="ready_to_build_year"
                  type="number"
                  step="1"
                  color={formState.errors['ready_to_build_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('ready_to_build_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>

            <Field
              id="commercial_operation_date"
              label={t('Commercial Operation Date')}
              error={
                formState.errors['commercial_operation_date_quarter'] ||
                formState.errors['commercial_operation_date_year']
              }
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="commercial_operation_date_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['commercial_operation_date_quarter'] ? 'error' : 'primary'}
                  registration={register('commercial_operation_date_quarter')}
                />
                <Input
                  id="commercial_operation_date_year"
                  type="number"
                  step="1"
                  color={formState.errors['commercial_operation_date_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('commercial_operation_date_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>
          </div>

          <Field
            id="turbine_capacity"
            label={t('Turbine Capacity')}
            error={formState.errors['turbine_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="turbine_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 123')}
              color={formState.errors['turbine_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('turbine_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="pump_capacity"
            label={t('Pump Capacity')}
            error={formState.errors['pump_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="pump_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 123')}
              color={formState.errors['pump_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('pump_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="generation_storage_capacity"
            label={t('Pump Capacity')}
            error={formState.errors['generation_storage_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="generation_storage_capacity"
              type="number"
              suffix="GWh"
              placeholder={t('Example: 356')}
              color={formState.errors['generation_storage_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('generation_storage_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="comments"
            label={t('Comments')}
            error={formState.errors['comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="comments"
              color={formState.errors['comments'] ? 'error' : 'primary'}
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
